import get from 'lodash.get';
import * as yup from 'yup';
import { Admin } from 'store/admins';
import { validateName, validateEmail, validatePassword, validatePin } from 'utils/validation';
import { ADMIN, READ_ONLY_ADMIN, MAINTENANCE, GLOBAL_ADMIN, INVALID } from './constants';
import { RoleItem, Form, InitialForm, ExtendedAdmin, ExtendedForm } from './types';

export const defaultRoles: RoleItem[] = [
  {
    name: 'Admin',
    code: 'ClientAdmin'
  },
  {
    name: 'Read-only Admin',
    code: 'ReadOnlyAdmin'
  },
  {
    name: 'Maintenance',
    code: 'MaintenanceAdmin'
  }
];

export const globalRole: RoleItem[] = [
  {
    name: 'Global Admin',
    code: 'GlobalAdmin'
  }
];

export const fullRoleList = defaultRoles.concat(globalRole);

export const validationSchema = yup.object().shape({
  name: validateName(),
  email: validateEmail(),
  password: validatePassword(),
  pinCode: validatePin()
});

export const getInitialCreateForm = (): Form => ({
  role: null,
  name: '',
  email: '',
  password: '',
  sendAlertEmails: false,
  pinCode: ''
});

export const getInitialEditForm = (admin: Admin) => ({
  name: admin.name,
  role: admin.role,
  sendAlertEmails: admin.sendAlertEmails,
  venueId: admin.venueId,
  password: '',
  email: admin.email,
  pinCode: admin.pinCode
});

/**
 * Checks to see if any of the (create) form values are empty.
 */
export const getHasEmptyInput = (form: Form) => {
  if (form.role === MAINTENANCE) delete form.password;
  return Object.values(form).some((value) => value === null || value === '');
};

/**
 * Checks to see if any of the (edit) form values are empty.
 */
export const getHasEmptyEditInput = (form: InitialForm, admin: Admin) => {
  let formObj: ExtendedForm = form;

  // IF ROLE IS MAINTENANCE OR (INITIAL ROLE AND CURRENT ROLE IS ADMIN) CREATE A NEW OBJECT WITH PASSWORD REMOVED
  if (!(admin.role === MAINTENANCE && [ADMIN, READ_ONLY_ADMIN, GLOBAL_ADMIN].includes(form.role))) {
    formObj = Object.keys(formObj).reduce<ExtendedForm>((obj, key) => {
      if (key !== 'password') obj[key as keyof InitialForm] = formObj[key as keyof InitialForm];
      return obj;
    }, {});
  }

  // IF ANY VALUE ON THE FORM OBJECT IS AN EMPTY STRING RETURN TRUE
  return Object.values(formObj).some((value) => value === '');
};

/**
 * Checks to see if any of validation object values has INVALID
 * @param {object} validation - validation object --> has the shape of validationSchema object
 */
export const getHasInvalidInput = (validation: { [x: string]: string }) => {
  const tempValidation = validation;
  return Object.values(tempValidation).some((value) => value === INVALID);
};

export const getHasInitialInput = (form: InitialForm, admin: Admin) => {
  const tempAdmin: Admin & { password?: string } = admin;

  // CREATES A NEW OBJECT FROM TEMP ADMIN WITH KEYS THAT ONLY EXIST ON THE FORM OBJECT
  const adminObj = Object.keys(tempAdmin).reduce<ExtendedAdmin>((obj, key) => {
    if (get(form, key as keyof InitialForm) !== undefined) obj[key as keyof Admin] = admin[key as keyof Admin];
    return obj;
  }, {});

  // IF EVERY VALUE ON ADMIN OBJECT MATCHES EVERY VALUE ON FORM OBJECT RETURN TRUE
  return Object.keys(adminObj).every((key) => form[key as keyof InitialForm] === adminObj[key as keyof ExtendedAdmin]);
};
